import { log } from "./log";
import { client } from "./supabase";

// Lectura/escritura de `alertas_recurrencia_descartadas`. La tabla base + RLS ya está aplicada en
// producción (migración 20260925d); la columna `posponer_hasta` es la que sigue ESCRITA SIN
// APLICAR (10.137, migración 20260926) — mientras no exista, `cargarDescartes` recibe error de
// PostgREST al pedirla, devuelve `null` (mismo camino que "tabla no existe"), y el botón
// "Postergar" del panel no se muestra — las otras 3 vías de apagado siguen funcionando igual.
//
// Ya NO existe el descarte permanente (Fase 3 del rediseño, 2026-09-26): toda fila representa una
// postergación con vencimiento. Ambos escritores (`postergarAlerta`/`cerrarAlertaHastaElAnoQueViene`,
// Fase E §15.3/§15.6/§15.9) son UPSERT, no INSERT — la PK es (pais, empresa_norm, mes, ano), así
// que volver a postergar/cerrar una empresa ya postergada antes (venció y volvió a aparecer)
// actualiza la MISMA fila en vez de chocar contra la PK.

export const cargarDescartes = async ({ pais }) => {
  const { data, error } = await client
    .from("alertas_recurrencia_descartadas")
    .select("empresa_norm, mes, ano, posponer_hasta")
    .eq("pais", pais);
  if (error) {
    log.warn("cargarDescartes", `alertas_recurrencia_descartadas no disponible todavía: ${error.message}`);
    return null;
  }
  return data || [];
};

const DIAS_POSTERGACION = 7;

// Fuente ÚNICA del UPSERT — Postergar (§15.6, +7 días) y el check "ya en contacto"/"propuesta en
// carrera" (§15.3/§15.4/§15.9, fin del mes del aniversario) son dos VALORES distintos de
// `posponer_hasta` sobre la MISMA tabla/fila — nunca dos mecanismos separados.
const guardarPosponerHasta = async ({ pais, empresaNorm, mes, ano, usuarioId, posponerHasta }) => {
  const { error } = await client.from("alertas_recurrencia_descartadas").upsert(
    {
      pais,
      empresa_norm: empresaNorm,
      mes,
      ano,
      descartado_por: usuarioId,
      descartado_en: new Date().toISOString(),
      posponer_hasta: posponerHasta,
    },
    { onConflict: "pais,empresa_norm,mes,ano" }
  );
  if (error) throw error;
};

export const postergarAlerta = async ({ pais, empresaNorm, mes, ano, usuarioId, dias = DIAS_POSTERGACION }) => {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + dias);
  try {
    await guardarPosponerHasta({
      pais,
      empresaNorm,
      mes,
      ano,
      usuarioId,
      posponerHasta: fecha.toISOString().slice(0, 10),
    });
  } catch (error) {
    log.error("postergarAlerta", error);
    throw error;
  }
};

/**
 * El check "Cliente y agencia ya en contacto" / "Ya hay una propuesta en carrera" (§15.3/§15.4,
 * §15.9) — tildarlo cierra la fila hasta el año que viene: `posponer_hasta` = el ÚLTIMO día del
 * mes del aniversario (`mesObjetivo`/`anoObjetivo`), no +7 días. Como ese mes es exactamente el
 * mismo que define el `fin` de la ventana de encendido (`ventanaEvento`, Commit 2), la fila
 * desaparece de inmediato y no vuelve a aparecer hasta el próximo ciclo — nunca se va sola antes
 * de esto (§15, principio: "el Radar no desaparece solo nunca").
 */
export const cerrarAlertaHastaElAnoQueViene = async ({ pais, empresaNorm, mesObjetivo, anoObjetivo, usuarioId }) => {
  const finDeMes = new Date(anoObjetivo, mesObjetivo, 0); // último día de mesObjetivo (1-based)
  try {
    await guardarPosponerHasta({
      pais,
      empresaNorm,
      mes: mesObjetivo,
      ano: anoObjetivo,
      usuarioId,
      posponerHasta: finDeMes.toISOString().slice(0, 10),
    });
  } catch (error) {
    log.error("cerrarAlertaHastaElAnoQueViene", error);
    throw error;
  }
};
